import { getCubaREST, MainStore } from "@cuba-platform/react";
import { WrappedFormUtils } from "antd/lib/form/Form";
import { parseToJsonFromFieldValue } from "../../../components/MultiFileUpload";
import { setItemPropertiesValue } from "../../../util";
import { SerializedEntity } from "@cuba-platform/rest";

/**
 * 1. Достаём данные из формы + приводим файлы
 */
export function getUpdateEntityData<T>(
  form: WrappedFormUtils,
  fields: string[],
  mainStore: MainStore,
  entityName: string,
): Record<string, T> {
  const raw = form.getFieldsValue(fields);

  const metaClass = mainStore?.metadata?.find(
    (mci) => mci.entityName === entityName,
  );

  if (metaClass?.properties) {
    metaClass.properties
      .filter(
        (prop) =>
          prop.type === "sys$FileDescriptor" &&
          (prop.cardinality === "ONE_TO_MANY" ||
            prop.cardinality === "MANY_TO_MANY") &&
          fields.includes(prop.name),
      )
      .forEach((prop) => {
        const files = raw[prop.name];
        if (files) {
          raw[prop.name] = parseToJsonFromFieldValue(files);
        }
      });
  }

  return raw;
}

/**
 * 2. Преобразуем данные под метаданные (даты, связи, null и т.д.)
 */
export function prepareEntityForCommit<T extends object>(
  mainStore: MainStore,
  entityName: string,
  data: T,
  patch: Partial<T>, // <-- строго по полям T
): T {
  return setItemPropertiesValue(mainStore, entityName, data, patch);
}

/**
 * 3. Отправляем данные в CUBA
 */
export async function commitEntityData<T>(
  entityName: string,
  id: string,
  preparedData: T,
) {
  return getCubaREST().commitEntity(entityName, {
    id,
    ...preparedData,
  });
}

type WithId = { id?: string };

type BaseStepsArgs<T extends WithId> = {
  form: WrappedFormUtils<any>;
  fields: string[];
  mainStore: MainStore;
  initialValue: { entityName: string; id: string };
  dataInstance?: SerializedEntity<WithId & T>;
};

type BaseSteps = {
  getRawData: () => Record<string, unknown>;
  prepareData: (rawData: any) => any;
  commitData: (preparedData: any) => Promise<any>;
};

export const getBaseSteps = <T extends WithId>({
  form,
  fields,
  mainStore,
  initialValue,
  dataInstance,
}: BaseStepsArgs<T>): BaseSteps => {
  return {
    getRawData: () =>
      getUpdateEntityData(form, fields, mainStore, initialValue.entityName),

    prepareData: (rawData) =>
      prepareEntityForCommit(
        mainStore,
        initialValue.entityName,
        dataInstance || ({} as SerializedEntity<T>),
        rawData,
      ),

    commitData: async (preparedData) => {
      return await commitEntityData(
        initialValue.entityName,
        dataInstance?.id,
        preparedData,
      );
    },
  };
};
